<template>
  <v-page page="Tasks" title="Tasks">
    <template slot="toolbar">
      <v-spacer></v-spacer>
      <v-btn v-if="$can({ task: 'write' })" data-test="createTaskType" @click="createNew()">
        <v-icon>add</v-icon>New Config
      </v-btn>
    </template>
    <v-main v-if="loaded">
      <v-layout align-start>
        <v-flex xs3 style="border-right: 1px solid #eee">
          <v-list two-line class="pa-0">
            <v-subheader>
              <v-text-field v-model="search"
                            placeholder="Search..."
                            prepend-inner-icon="search"
                            clearable
              ></v-text-field>
            </v-subheader>
            <div class="scroll-y grey lighten-4">
              <v-list-items v-model="selected"
                            :items="activeCuratedTasks"
                            item-title="name"
                            item-subtitle="short_code"
                            :persist-actions="[ updateStatus ]"
                            data-test="activeTasks">
                <template v-slot:header>
                  <v-icon>layers</v-icon> Active ({{ activeCuratedTasks.length }})
                </template>
                <template v-for="item in activeCuratedTasks" v-slot:[`actions-${item.id}`]>
                  <template v-if="$can({ task: 'write' })">
                    <template v-if="updateStatus !== item.id">
                      <v-btn v-if="item.status === 'expire'" :key="`item-enable-${item.id}`" @click="updateTaskTypeStatus(item.id)" icon ripple :data-test="`${item.name}EnableButton`">
                        <v-icon :color="item.id === selected ? 'white' : 'grey darken-2'">history</v-icon>
                      </v-btn>
                      <v-btn :key="`item-disable-${item.id}`" @click="updateTaskTypeStatus(item.id, false)" icon ripple :data-test="`${item.name}DisableButton`">
                        <v-icon :color="item.id === selected ? 'white' : 'grey darken-2'">highlight_off</v-icon>
                      </v-btn>
                    </template>
                    <v-progress-circular v-else :key="`item-enabling-disabling-${item.id}`"
                                         indeterminate
                                         color="white"
                    ></v-progress-circular>
                  </template>
                </template>
              </v-list-items>
              <v-list-items v-model="selected"
                            :items="disabledCuratedTasks"
                            item-title="name"
                            item-subtitle="short_code"
                            :persist-actions="[ updateStatus ]"
                            data-test="disabledTasks">
                <template v-slot:header>
                  <v-icon>layers_clear</v-icon> Disabled ({{ disabledCuratedTasks.length }})
                </template>
                <template v-for="item in disabledCuratedTasks" v-slot:[`actions-${item.id}`]>
                  <template v-if="$can({ task: 'write' })">
                    <v-btn v-if="updateStatus !== item.id" :key="`item-enable-${item.id}`" @click="updateTaskTypeStatus(item.id)" icon ripple :data-test="`${item.name}EnableButton`">
                      <v-icon :color="item.id === selected ? 'white' : 'grey darken-2'">history</v-icon>
                    </v-btn>
                    <v-progress-circular v-else :key="`item-enabling-${item.id}`"
                                         indeterminate
                                         color="white"
                    ></v-progress-circular>
                  </template>
                </template>
              </v-list-items>
            </div>
          </v-list>
        </v-flex>
        <template v-if="selected !== null || creating">
          <v-flex xs5 class="pt-2 white" style="min-height: 719px">
            <v-layout>
              <v-flex class="mx-2 mt-1 px-2">
                <v-text-field label="Name"
                              v-model="name"
                              :rules="$can({ task: 'write' }) ? [
                                v => !!v || 'Name is required',
                                v => Object.values(this.$store.state.tasks.taskTypes).find(taskType => taskType.id !== selectedTaskTypeId && taskType.name === v) === undefined || 'Task type name already exists'
                              ]: []"
                              data-test="taskNameField"
                              :readonly="!$can({ task: 'write' })"/>
                <v-text-field label="Short Code"
                              v-model="short_code"
                              :rules="$can({ task: 'write' }) ? [
                                v => !!v || 'Short code is required',
                                v => Object.values(this.$store.state.tasks.taskTypes).find(taskType => taskType.id !== selectedTaskTypeId && taskType.short_code === v) === undefined || 'Task type short code already exists'
                              ]: []"
                              data-test="taskShortCodeField"
                              :readonly="!$can({ task: 'write' })"/>
                <v-text-field label="Duration (Minutes)"
                              v-model="duration"
                              type="number"
                              :rules="$can({ task: 'write' }) ? [
                                v => !!v || 'Duration is required'
                              ]: []"
                              data-test="taskDurationField"
                              :readonly="!$can({ task: 'write' })"/>
                <h5 class="display-1">
                  Config Schema
                </h5>
                <json-textarea label="JSON Schema"
                                        :value.sync="editableSchema"
                                        data-test="taskJsonSchemaField"
                                        :readonly="!$can({ task: 'write' })"/>
              </v-flex>
            </v-layout>
            <v-toolbar v-if="$can({ task: 'write' })" color="white" class="elevation-0">
              <v-spacer></v-spacer>
              <v-btn v-if="creating" color="gray" @click="creating = false" text>Cancel</v-btn>
              <v-btn color="success" @click="saveSchema" :disabled="!isSchemaValid" data-test="saveTaskButton">Save</v-btn>
            </v-toolbar>
          </v-flex>
          <v-flex xs4 class="ml-2 px-3 py-2 white">
            <h5 class="display-1 mb-3" v-if="$can({ task: 'write'})">Form Preview</h5>
            <task-json-editor
              v-if="!isSchemaEmpty"
              :key="`editor-${refresh}`"
              :select-previous="$can({ task: 'write' }) ? selectPrevious : true"
              :task-type-id="selectedTaskTypeId"
              @selectedExistingTask="onSelectedExistingTask"
              v-model="result"
              :schema="JSON.parse(schema)"
              :readonly="!$can({ task: 'write' })">
            </task-json-editor>
            <div class="my-3" v-else>None</div>
            <v-divider class="my-4" v-if="$can({ task: 'write'})"></v-divider>
            <h5 class="display-1">Task Config Preview</h5>
            <clipboard :value="jsonValue" v-if="!isSchemaEmpty">
              <pre class="pa-2">{{ preview }}</pre>
            </clipboard>
            <div class="my-3" v-else>None</div>
          </v-flex>
        </template>
        <template v-else>
          <v-flex xs9 class="text-sm-center pt-5">
              <template v-if="curatedTasks.length === 0 && !['', null].includes(this.search)">
                <v-btn @click="createNew(search)" color="success darken-2"><v-icon>add</v-icon> {{ search }}</v-btn>
              </template>
              <template v-else>
            <v-icon color="grey lighten-1" large>folder</v-icon>
            <p class="grey--text lighten-1">Select a Task</p>
              </template>
          </v-flex>
        </template>
      </v-layout>
    </v-main>
    <v-progress-linear v-else indeterminate></v-progress-linear>
  </v-page>
</template>

<script>
import VPage from '@/layouts/Default'
import TaskJsonEditor from '@/components/JsonEditor/TaskJsonEditor'
import Clipboard from '@/components/Clipboard'
import JsonTextarea from '@/components/JsonEditor/JsonTextarea'
import { Bus } from '@/bus'
import VListItems from '@/components/ListItems/ListItems'
import { updateTaskTypeStatus } from '@/services/Tasks'
import { isPastDate, datetimeForHumans } from '@/services/time'
import moment from 'moment'

export default {
  components: {
    JsonTextarea,
    VPage,
    TaskJsonEditor,
    Clipboard,
    VListItems
  },
  data () {
    return {
      creating: false,
      selected: null,
      updateStatus: null,
      search: null,
      hover: null,
      showTrashed: false,
      name: null,
      short_code: null,
      duration: null,
      editableSchema: '{}',
      schema: '{}',
      result: {},
      refresh: 0,
      selectedTaskType: 1,
      selectPrevious: false,
      selectedTaskTypeId: 0,
      loaded: false,
      advancedOptions: [false],
      template: {
        name: null,
        short_code: null,
        duration: null,
        json_schema: {
          $schema: 'http://json-schema.org/draft-07/schema#',
          type: 'object',
          title: '',
          required: [],
          properties: {}
        },
        select_previous: false
      }
    }
  },
  mounted () {
    this.refreshTaskTypes()
  },
  computed: {
    /**
     * Task types list
     *
     * @returns {Array}
     */
    taskTypes () {
      return Object.values(this.$store.state.tasks.taskTypes).map(task => {
        let description = `${task.duration} m`
        let status = task.expires_at === null ? 'active' : 'disabled'

        if (task.expires_at && !isPastDate(task.expires_at)) {
          status = 'expire'
          description += ` [EXP: ${moment.utc().format(datetimeForHumans)} (UTC)]`
        }
        return { ...task, description, status }
      }).sort(this.sorting)
    },
    /**
     * Curated task types list with search query
     */
    curatedTasks () {
      const regex = new RegExp(this.search, 'i')
      return ['', null].includes(this.search)
        ? this.taskTypes
        : this.taskTypes.filter(task => task.name.search(regex) !== -1)
    },
    activeCuratedTasks () {
      return this.curatedTasks.filter(task => [ 'active', 'expire' ].indexOf(task.status) !== -1)
    },
    disabledCuratedTasks () {
      return this.curatedTasks.filter(task => task.status === 'disabled')
    },
    /**
     * The raw config json value outputted by the form (good for copying to clipboard)
     *
     * @returns {string}
     */
    jsonValue () {
      return JSON.stringify(this.result)
    },
    /**
     * The nicely formatted json value outputted by the form (good for displaying on screen)
     *
     * @returns {string}
     */
    preview () {
      return JSON.stringify(this.result, null, 2)
    },
    /**
     * Returns true if the current task schema does not contain any fields
     *
     * @returns {boolean}
     */
    isSchemaEmpty () {
      return this.schema === '{}'
    },
    /**
     * Returns true if the current task schema is valid JSON
     *
     * @returns {boolean}
     */
    isSchemaValid () {
      try {
        return typeof JSON.parse(this.editableSchema) === 'object'
      } catch (e) {
        return false
      }
    }
  },
  methods: {
    /**
     * Create new task type
     *
     * @params {String} name
     */
    createNew (name = null) {
      this.selected = null
      this.creating = true
      this.onChangeTaskType({ ...this.template, name, advancedOptions: [ true ] })
    },
    /**
     * Update task type status (enable/disable)
     *
     * @params {Integer} id
     * @params {Boolean} isEnabling
     */
    updateTaskTypeStatus (id, isEnabling = true) {
      if (this.updateStatus !== null) {
        return
      }

      this.updateStatus = id

      updateTaskTypeStatus(id, isEnabling)
        .then(response => {
          Bus.$emit('alert', { color: 'success', message: `${this.taskTypes.find(task => task.id === id).name} ${isEnabling ? 'enabled' : 'disabled'} successfully!` })
        })
        .catch(({ message }) => {
          Bus.$emit('alert', { color: 'error', message })
        })
        .finally(() => {
          this.updateStatus = null
          this.refreshTaskTypes()
        })
    },
    /**
     * Save the task type schema to the database
     */
    saveSchema () {
      const isCreate = this.selectedTaskTypeId === undefined

      this.$store.dispatch(isCreate ? 'tasks/createTaskType' : 'tasks/changeTaskTypeSchema', {
        taskId: this.selectedTaskTypeId,
        name: this.name,
        duration: this.duration,
        shortCode: this.short_code,
        jsonSchema: JSON.parse(this.schema),
        selectPrevious: this.selectPrevious
      })
        .then((response) => {
          Bus.$emit('alert', { color: 'success', message: `The task type configuration has been ${isCreate ? 'created' : 'updated'}` })
          this.refreshTaskTypes(response.data.id)
        })
        .catch(({ message }) => {
          Bus.$emit('alert', { color: 'error', message })
        })
    },
    /**
     * Called when a previously created task is selected
     *
     * @param taskConfig
     */
    onSelectedExistingTask (taskConfig) {
      this.result = taskConfig
    },
    /**
     * Called when the selected task type is changed
     *
     * @param taskType
     */
    onChangeTaskType (taskType) {
      this.advancedOptions = [ this.creating ]
      const schema = taskType['json_schema']
      this.editableSchema = JSON.stringify(schema, null, '\t')
      this.name = taskType['name']
      this.duration = taskType['duration']
      this.short_code = taskType['short_code']
      this.selectPrevious = taskType['select_previous']
      this.selectedTaskTypeId = taskType.id
      this.refreshForm()
    },
    /**
     * Reset outputted config and force the generated form to refresh
     */
    refreshForm () {
      if (this.isSchemaValid) {
        this.result = {}
        this.schema = this.editableSchema
        this.refresh++
      }
    },
    /**
     * Refresh task types
     *
     * @returns {void}
     */
    refreshTaskTypes (selectedId) {
      this.loaded = false

      // Refresh task types
      this.$store.dispatch('tasks/fetchTaskTypes').then(() => {
        let selectedTaskTypeIndex = this.taskTypes.findIndex(taskType => taskType.id === selectedId)

        this.selectedTaskType = selectedTaskTypeIndex !== -1
          ? selectedTaskTypeIndex + 1
          : 1
      })
        .catch(({ message }) => {
          Bus.$emit('alert', { color: 'error', message })
        })
        .finally(() => {
          // Initialize the json editor
          this.loaded = true
          this.selected = null
        })
    },
    /**
     * Sort function for task types
     */
    sorting (task1, task2) {
      if (task1.name.toLowerCase() < task2.name.toLowerCase()) {
        return -1
      }

      return (task1.name.toLowerCase() > task2.name.toLowerCase()) ? 1 : 0
    },
    expiration (expiresAt) {
      if (expiresAt !== null && !isPastDate(expiresAt)) {
        return moment.utc(expiresAt).format(datetimeForHumans)
      }
    }
  },
  watch: {
    editableSchema () {
      this.refreshForm()
    },
    selectPrevious () {
      this.refreshForm()
    },
    search () {
      // This to clear selected task type if it does not exist in curated list
      if (this.curatedTasks.find(task => task.id === this.selected) === undefined) {
        this.selected = null
      }
    },
    /**
     * Update forms according to selected task type
     */
    selected () {
      const task = this.curatedTasks.find(task => task.id === this.selected)

      if (this.selected !== null) {
        this.creating = false
      }

      if (task) {
        this.onChangeTaskType(task)
      }
    },
    /**
     * Update schema when short_code updates
     */
    short_code () {
      let schema = JSON.parse(this.editableSchema)
      schema.title = this.short_code ? `${this.short_code.toUpperCase()} Schema` : ''

      this.editableSchema = JSON.stringify(schema, null, '\t')

      this.refreshForm()
    }
  }
}
</script>
